/* eslint-disable react/require-default-props */
import React from "react";
import { Formik } from "formik";
import { Button, Form, MultiSelect, Select, SelectItem, TextInput } from "carbon-components-react";

import { docTypeSelectOpts, docTypeToHumanStr } from "underwriter_dashboard/shared";

import { AppShape, DocShape } from "@eave/shared/types/api";
import { MultiSelectSelectedItems } from "@eave/shared/types/forms";
import { borrowerEntitySelectOptionsFromApp } from "@eave/shared/utils/borrowers";
import Spacer from "@eave/shared/components/Spacer";

import {
  displayBoolValue,
  makeEvent,
  normalizeBoolEvent,
} from "web_modules/shared/FormHelpers/carbon-helpers";

import RevertDocValue from "./RevertDocValue";
import { yesNoOptions } from "./values";
import { DocumentOwnerOption } from "./types";
import { cleanFileName } from "./helpers";

const styles = require("../docUploaderStyles.module.scss");

interface DocDataFormProps {
  app: AppShape;
  hideButton?: boolean;
  doc: DocShape;
  initialValues: any;
  ownerOptions: DocumentOwnerOption[];
  onSubmit: any;
  updateParentState: Function;
}

const ownerDisplayText = (doc, ownerOptions) => {
  const matches = ownerOptions.filter(
    option =>
      option.value.owner_entity_type === doc.owner_entity_type &&
      option.value.owner_entity_id === doc.owner_entity_id,
  );
  if (!matches || !matches.length) {
    return "";
  }

  const ownerEntity = matches[0];
  if (ownerEntity !== null) {
    return ownerEntity.label;
  }
  return "";
};

export const DocDataForm = ({
  app,
  hideButton = true,
  doc,
  initialValues,
  ownerOptions,
  onSubmit,
  updateParentState,
}: DocDataFormProps) => {
  return (
    <Formik enableReinitialize onSubmit={onSubmit} initialValues={initialValues}>
      {({ values, handleChange, handleBlur, setValues, handleSubmit }) => {
        const {
          type,
          revised_filename,
          owner_entity_id,
          borrower_entity, // aka "owner"
          newVisibility, // aka "used?"
          newUsed, // aka "borrower visible"
          newOwner, // only used for the select values
        } = values;

        const updateFields = e => {
          handleChange(e); // .update local form state
          // The modal version is self-contained and doesn't need to update the parent
          if (updateParentState) {
            updateParentState({ ...values, [e.target.name]: e.target.value }); // update parent form state
          }
        };

        const updateFilename = e => {
          e.target.value = cleanFileName(e.target.value);
          updateFields(e);
        };

        const revert = (name: string, value: string) => {
          setValues({ ...values, [name]: value });
          if (updateParentState) {
            updateParentState({ ...values, [name]: value }); // update parent form state
          }
        };

        const changesWereMade =
          initialValues.type !== type ||
          initialValues.revised_filename !== revised_filename ||
          initialValues.owner_entity_id !== owner_entity_id ||
          initialValues.borrower_entity !== borrower_entity ||
          initialValues.newVisibility !== newVisibility ||
          initialValues.newUsed !== newUsed;

        const getBorrowerEntityBitmapIntValue = (
          selectedEntities: MultiSelectSelectedItems[],
        ): number => {
          const entitySum = selectedEntities.reduce(
            (sum: number, selectedEntity) => sum + selectedEntity.value,
            0,
          );
          return parseInt(entitySum, 10);
        };

        const getInitiallySelectedBorrowerEntitiesForDocument = (
          entitySum: number,
        ): MultiSelectSelectedItems[] => {
          const selectableBorrowerEntities = borrowerEntitySelectOptionsFromApp(app);
          const initiallySelectedBorrowerEntityValues = [];

          let i = 0;
          let currentEntitySum = entitySum;
          while (currentEntitySum) {
            if (currentEntitySum & 1) {
              initiallySelectedBorrowerEntityValues.push(1 << i);
            }

            currentEntitySum >>= 1;
            i += 1;
          }
          return selectableBorrowerEntities.filter(entity =>
            initiallySelectedBorrowerEntityValues.includes(entity.value),
          );
        };

        return (
          <div className={styles.newInfo}>
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <Select
                light
                id="type"
                name="type"
                labelText="Document Type"
                placeholder="Select doc type"
                value={type}
                onChange={updateFields}
                onBlur={handleBlur}
              >
                <SelectItem text="" value="" />
                {docTypeSelectOpts.map(opt => (
                  <SelectItem key={opt.value} text={opt.label} value={opt.value} />
                ))}
              </Select>
              {initialValues.type !== type && (
                <RevertDocValue
                  oldValue={docTypeToHumanStr(doc.type)}
                  revert={() => revert("type", initialValues.type)}
                />
              )}
              <Spacer />
              <TextInput
                light
                id="revised_filename"
                name="revised_filename"
                value={revised_filename}
                labelText="Filename"
                className={styles.input}
                placeholder="Enter filename"
                onChange={updateFilename}
                onBlur={handleBlur}
              />
              {initialValues.revised_filename !== revised_filename && (
                <RevertDocValue
                  oldValue={doc.filename}
                  revert={() => revert("revised_filename", initialValues.revised_filename)}
                />
              )}
              <Spacer />
              <Select
                light
                id="owner_entity_id"
                name="owner_entity_id"
                labelText="Owner Entity"
                placeholder="Select owner"
                value={newOwner || ""}
                onChange={e => {
                  const selectedOwner = JSON.parse(e.target.value);

                  const updated = {
                    ...values,
                    owner_entity_id: selectedOwner.owner_entity_id,
                    owner_entity_type: selectedOwner.owner_entity_type,
                    newOwner: JSON.stringify(selectedOwner),
                  };

                  setValues(updated);

                  if (updateParentState) {
                    updateParentState(updated);
                  }
                }}
                onBlur={handleBlur}
              >
                <SelectItem
                  key="Invalid owner entity ID Option"
                  text="Select an owner entity"
                  value=""
                />
                {ownerOptions &&
                  ownerOptions.map(opt => (
                    <SelectItem
                      key={opt.value.owner_entity_id}
                      text={opt.label}
                      value={JSON.stringify(opt.value)}
                    />
                  ))}
              </Select>
              {initialValues.owner_entity_id !== owner_entity_id && (
                <RevertDocValue
                  oldValue={ownerDisplayText(doc, ownerOptions)}
                  revert={() => {
                    setValues({
                      ...values,
                      owner_entity_type: initialValues.owner_entity_type,
                      owner_entity_id: initialValues.owner_entity_id,
                    });
                    if (updateParentState) {
                      updateParentState({
                        owner_entity_id: initialValues.owner_entity_id,
                        owner_entity_type: initialValues.owner_entity_type,
                      });
                    }
                  }}
                />
              )}
              <Spacer />
              <MultiSelect
                light
                id="borrower_entity"
                name="borrower_entity"
                titleText="Select owner(s)"
                label="Select owner(s)"
                value={borrower_entity}
                onChange={e => {
                  updateFields(
                    makeEvent("borrower_entity", getBorrowerEntityBitmapIntValue(e.selectedItems)),
                  );
                }}
                onBlur={handleBlur}
                initialSelectedItems={getInitiallySelectedBorrowerEntitiesForDocument(
                  initialValues.borrower_entity,
                )}
                items={borrowerEntitySelectOptionsFromApp(app)}
              />
              {initialValues.borrower_entity !== borrower_entity && (
                <RevertDocValue
                  oldValue={doc.borrower_entity ? `Borrower ${doc.borrower_entity}` : "-"}
                  revert={() => revert("borrower_entity", initialValues.borrower_entity)}
                />
              )}
              <Spacer />
              <Select
                light
                id="newUsed"
                name="newUsed"
                labelText="Used?"
                value={newUsed}
                onChange={e => {
                  updateFields(makeEvent("newUsed", normalizeBoolEvent(e.target.value)));
                }}
                onBlur={handleBlur}
              >
                {yesNoOptions.map(opt => (
                  <SelectItem key={opt.value} text={opt.label} value={opt.value} />
                ))}
              </Select>
              <Spacer />
              {initialValues.newUsed !== newUsed && (
                <RevertDocValue
                  oldValue={doc.used ? "Yes" : "No"}
                  revert={() => revert("newUsed", initialValues.newUsed)}
                />
              )}
              <Spacer />
              <Select
                light
                id="newVisibility"
                name="newVisibility"
                labelText="Borrower Visible"
                value={displayBoolValue(newVisibility)}
                onChange={e => {
                  updateFields(makeEvent("newVisibility", normalizeBoolEvent(e.target.value)));
                }}
                onBlur={handleBlur}
              >
                {yesNoOptions.map(opt => (
                  <SelectItem key={opt.value} text={opt.label} value={opt.value} />
                ))}
              </Select>
              {initialValues.newVisibility !== newVisibility && (
                <RevertDocValue
                  oldValue={doc.visible ? "Yes" : "No"}
                  revert={() => revert("newVisibility", initialValues.newVisibility)}
                />
              )}
              {!hideButton && (
                <>
                  <div style={{ marginBottom: "10px" }} />
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      kind="primary"
                      disabled={!changesWereMade} // disable if no changes made
                      type="submit"
                    >
                      {changesWereMade ? "Save" : "No changes"}
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};

export default DocDataForm;
